import React from 'react'
import styles from './button.module.scss'
import { type IconImage } from '../Icon/icon.types'

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Props extends a DOM attributes interface; interface extension is idiomatic here
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant:
  | 'default'
  | 'rounded'
  | 'big'
  | 'secondary'
  | 'filter'
  | 'circle'
  | 'textual'
  | 'pill'
  label?: React.ReactNode
  bgColor?: 'cream' | 'olive' | 'kale' | 'white' | 'grana' | 'light-cream' | 'corn' | 'transparent'
  txtColor?: 'cream' | 'olive' | 'kale' | 'white' | 'light-kale'
  txtClassName?: string
  iconImage?: IconImage
  iconPosition?: 'left' | 'right'
  border?: boolean
  borderColor?: 'olive' | 'kale' | 'cream' | 'white' | 'light-cream' | 'grana'
  size?: 'large' | 'medium' | 'small'
  bgDisabledColor?: 'cream' | 'light-cream' | 'light-kale' | 'white'
  txtDisabledColor?: 'light-kale' | 'white'
}

type ButtonVariant = ButtonProps['variant']

/**
 * Radius per variant — one class per element, never a base plus an override.
 * Two global classes on the same element would be decided by the order of
 * borders.scss, which nothing here shows; a single one cannot tie with itself.
 *
 * `textual` is the one radius no utility publishes (`0`); it stays in the
 * sheet, where it outranks anything global.
 */
const RADIUS_BY_VARIANT: Record<ButtonVariant, string> = {
  default: 'border-s',
  rounded: 'border-xxl',
  big: 'border-xxl',
  secondary: 'border-xxl',
  filter: 'border-xxl',
  pill: 'border-xxl',
  circle: 'border-circle',
  textual: '',
}

/**
 * The four variants that draw a border, and the width utility each one uses.
 * The widths MUST travel with the colour: they are `border` shorthands, so
 * leaving them in the sheet as a compound (0,2,0) would set `border-color:
 * currentColor` above the colour class (0,1,0), and every white or cream
 * border would silently turn into the text colour.
 *
 * `circle` maps to no utility because none publishes its 0.25rem — its width
 * stays in the sheet, declared as width plus style so it never touches the
 * colour. It is still a key here: membership is what says a variant takes a
 * border at all, and the other four ignore `borderColor` exactly as they did
 * when the colours were compound selectors.
 */
const BORDER_WIDTH_BY_VARIANT: Partial<Record<ButtonVariant, string>> = {
  secondary: 'border-4',
  filter: 'border-1',
  pill: 'border-4 border-2-s',
  circle: '',
}

/**
 * Colours a variant does NOT accept, even though `borders.scss` publishes them.
 *
 * This map is the only place that excludes `grana` from `secondary` —
 * `borders.scss` publishes every `border-*` utility, `border-grana` included,
 * so without this entry a `borderColor="grana"` would be silently granted, and
 * that is a behaviour change: 8 of the 896 variant × border × colour × size
 * combinations. The omission may well be an oversight, but granting it is a
 * deliberate decision that awaits the kit owner's ratification, not something
 * a refactor gets to make on the way past. Unreachable today — nothing in the
 * app passes `border`.
 *
 * !TODO: DELETE this map (and its spec) when the kit unifies the border
 * palette across variants.
 */
const COLOURS_NOT_TAKEN_BY_VARIANT: Partial<Record<ButtonVariant, ReadonlyArray<string>>> = {
  secondary: ['grana'],
}

export const Button: React.FC<ButtonProps> = ({
  variant,
  label,
  bgColor,
  txtColor,
  txtClassName = '',
  iconImage,
  iconPosition,
  border,
  borderColor,
  size,
  disabled,
  className,
  children,
  bgDisabledColor = 'cream',
  txtDisabledColor = 'light-kale',
  ...props
}) => {
  const effectiveBg = disabled ? bgDisabledColor : bgColor
  const effectiveTxt = disabled ? txtDisabledColor : txtColor
  const borderWidth = border ? BORDER_WIDTH_BY_VARIANT[variant] : undefined

  const classes = [
    styles.btn,
    styles[`btn--${variant}`],
    'cursor-pointer',
    'flx-j-c',
    'flx-a-c',
    // One gap class per variant, not base + override: within flex.scss the
    // smaller gaps are declared FIRST, so `flx-gap-4` could never win over a
    // `flx-gap-8` carried alongside it.
    variant === 'textual' ? 'flx-gap-4' : 'flx-gap-8',
    'flx-gap-4-s',
    RADIUS_BY_VARIANT[variant],
    effectiveBg ? `bg-${effectiveBg}` : '',
    effectiveTxt ? `txt-${effectiveTxt}` : '',
    'txt-action',
    'txt-action-s',
    iconImage ? styles['btn--icon'] : '',
    iconImage ? `icon-${iconImage}` : '',
    iconImage && iconPosition ? styles[`btn--icon-${iconPosition}`] : '',
    border ? styles['btn--border'] : '',
    borderWidth ?? '',
    borderWidth !== undefined &&
    borderColor &&
    !(COLOURS_NOT_TAKEN_BY_VARIANT[variant] ?? []).includes(borderColor)
      ? `border-${borderColor}`
      : '',
    size ? styles[`btn--size-${size}`] : '',
    className ?? '',
  ].filter(Boolean).join(' ')

  return (
    <button className={classes} {...props} disabled={disabled}>
      {(label != null || children != null) && (
        <span className={txtClassName}>{label ?? children}</span>
      )}
    </button>
  )
}
